import { FC, useCallback, useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';

import { PageLayout } from '../../layout/PageLayout';
import { loadFilters, loadLots } from '../../services/lots';

import { FilterData, FilterQuery, Props } from './types';
import { Filter } from './components/Filter';
import { SaleLot } from '../../types/sales';
import { AverageSalePrice } from './components/AverageSalePrice';
import { LotsList } from './components/LotsList';

const LotsPage: FC<Props> = () => {
  const [filterData, setFilterData] = useState<FilterData | null>(null);
  const [averageSalePrice, setAverageSalePrice] = useState<number | null>(null);
  const [filterQuery, setFilterQuery] = useState<Partial<FilterQuery> | null>(null);
  const [lots, setLots] = useState<SaleLot[] | null>(null);
  const [lotsCount, setLotsCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLotsLoading, setIsLotsLoading] = useState<boolean>(false);
  const [filterError, setFilterError] = useState(false);
  const [lotsError, setLotsError] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const requestId = useRef(0);

  const fetchFilterData = useCallback(async () => {
    try {
      setIsLoading(true);
      setFilterError(false);
      setFilterData(await loadFilters());
    } catch (error) {
      setFilterError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchLots = useCallback(async (filter: Partial<FilterQuery>) => {
    const currentRequest = ++requestId.current;
    try {
      setIsLotsLoading(true);
      setLotsError(false);
      const result = await loadLots(filter);
      if (currentRequest !== requestId.current) return;
      setFilterQuery(filter);
      setAverageSalePrice(result.averageSalePrice);
      setLots(result.lots);
      setLotsCount(result.countLots);
    } catch (error) {
      if (currentRequest === requestId.current) setLotsError(true);
    } finally {
      if (currentRequest === requestId.current) setIsLotsLoading(false);
    }
  }, []);

  const handleSubmit = useCallback(
    (filter: Partial<FilterQuery>) => {
      fetchLots(filter);
    },
    [fetchLots],
  );

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver(() => {
      window.parent.postMessage({
        type: 'container_height_changed',
        height: Math.ceil(element.getBoundingClientRect().height + element.offsetTop),
      }, '*');
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    fetchFilterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageLayout ref={containerRef}>
      <Filter data={filterData} onSubmit={handleSubmit} isLoading={isLoading} />
      {filterError && <Alert severity="error" sx={{ mt: 2 }} action={<Button onClick={fetchFilterData}>Повторить</Button>}>
        Не удалось загрузить фильтры. Попробуйте ещё раз.
      </Alert>}
      {lotsError && <Alert severity="error" sx={{ mt: 2 }}>Не удалось загрузить результаты. Попробуйте ещё раз.</Alert>}

      {averageSalePrice === null && lots === null && !isLotsLoading ? null : (
        <Box mt={2} position="relative">
          <Paper elevation={2}>
            {isLotsLoading && (
              <Box position="absolute" top={0} left={0} right={0}>
                <LinearProgress />
              </Box>
            )}

            <Box className="results-content" padding={3} aria-busy={isLotsLoading}>
              <AverageSalePrice
                value={averageSalePrice}
                maxSoldDaysAgo={Number(filterQuery?.maxSoldDaysAgo ?? null)}
                lotsCount={lotsCount}
              />

              <LotsList lots={lots} />
              {lots?.length === 0 && !isLotsLoading && <Alert severity="info">По выбранным условиям ничего не найдено. Измените фильтры.</Alert>}
            </Box>
          </Paper>
        </Box>
      )}
    </PageLayout>
  );
};

export { LotsPage };
